/** @jsxImportSource @emotion/react */

import { Interpolation, Theme, css } from "@emotion/react";
import { useRef, useState } from "react";
import { theme } from "styles/theme";
import Icon from "uis/Icon";
import InputField from "uis/InputField";
import { debounce } from "lodash";

type Props = {
    data?: string[];
    availableSearch?: boolean;
    availableEnter?: boolean;
    multiSelect?: boolean;
    emptyPlaceholder?: string;
    styles?: {
        container?: Interpolation<Theme>;
        input?: Interpolation<Theme>;
        label?: Interpolation<Theme>;
        selectContainer?: Interpolation<Theme>;
        iconContainer?: Interpolation<Theme>;
    };
    width?: number | string;
    id: number | string;
    onChange?: (value: string[]) => void;
    defaultValue?: string;
    errorText?: string;
    selectedData?: string[];
    renderIcon?: () => JSX.Element;
    placeholder?: string;
};

export default function Select(props: Props) {
    const inputRef = useRef<string>();

    const {
        styles,
        onChange = () => {},
        defaultValue,
        data,
        selectedData = [],
        renderIcon,
        emptyPlaceholder,
        id,
        width,
        availableSearch = false,
        multiSelect = false,
        placeholder,
        availableEnter = false,
    } = props;

    const [isShowAll, setIsShowAll] = useState<boolean>(false);
    const [inputValue, setInputValue] = useState<string>("");

    window.addEventListener("click", (e: any) => {
        if (multiSelect && e.target.classList.contains("select-list")) {
            return;
        } else if (multiSelect && e.target.classList.contains(`select-input-${id}`)) {
            return;
        } else if (!e.target?.classList.contains(`select-${id}`)) {
            setIsShowAll(false);
        }
    });

    return (
        <div
            css={[
                css`
                    cursor: pointer;
                    position: relative;
                    width: ${width};
                `,
            ]}
        >
            <div
                className={`select-${id}`}
                onClick={() => {
                    setIsShowAll(!isShowAll);
                }}
                css={[
                    css`
                        padding: 14px 16px;
                        border-radius: 12px;
                        display: flex;

                        justify-content: space-between;
                        align-items: center;
                        background-color: ${isShowAll ? theme.neutral100 : theme.neutral50};
                        &:hover {
                            background-color: ${theme.neutral100};
                        }
                    `,

                    styles?.container,
                ]}
            >
                <p
                    className={`select-${id}`}
                    css={[
                        css`
                            font-size: 18px;
                            font-weight: 400;
                            color: ${theme.neutral900};
                        `,
                        styles?.label,
                    ]}
                >
                    {selectedData.length > 0 && selectedData[0] !== "" ? selectedData.toString() : defaultValue}
                </p>
                {renderIcon && renderIcon()}
                {!renderIcon && (
                    <div
                        onClick={() => {
                            setIsShowAll(!isShowAll);
                        }}
                        css={[
                            css`
                                transform: ${isShowAll ? `rotate(180deg)` : `rotate(0)`};
                                transition: transform 0.5s;
                                display: flex;
                                align-items: center;
                            `,
                            styles?.iconContainer,
                        ]}
                    >
                        <Icon name="top-arrow" color={theme.neutral900} />
                    </div>
                )}
            </div>

            <ul
                className="options"
                css={[
                    css`
                        flex-direction: column;
                        list-style: none;
                        padding-left: 0;
                        display: ${isShowAll ? "flex" : "none"};
                        background-color: white;
                        z-index: 1;

                        position: absolute;
                        top: 40px;
                        padding: 16px 12px;
                        max-height: 200px;
                        overflow-y: auto;
                        min-width: ${width};
                        width: max-content;
                        border-radius: 14px;
                        border: none;
                        box-shadow: 0px 4px 20px 0px rgba(69, 76, 83, 0.15);
                    `,
                    styles?.selectContainer,
                ]}
            >
                {availableSearch ? (
                    <InputField
                        id={`${id}`}
                        placeholder={placeholder}
                        onChange={(e) => {
                            inputRef.current = e;
                        }}
                        onChangeDebounce={(e) => setInputValue(e)}
                        onKeyDown={(e) => {
                            if (availableEnter && inputRef.current && e.key === "Enter") onChange([inputRef.current]);
                        }}
                    />
                ) : null}
                {!data || data.length === 0 ? (
                    <p
                        css={css`
                            color: ${theme.neutral400};
                        `}
                    >
                        {emptyPlaceholder}
                    </p>
                ) : null}
                {data &&
                    data
                        .filter((value) => `${value}`.includes(inputValue || ""))
                        .map((item, index) => {
                            return (
                                <li
                                    className="select-list"
                                    css={css`
                                        padding: 14px 12px;
                                        border-radius: 10px;
                                        font-size: 16px;
                                        &:hover {
                                            background-color: ${theme.neutral50};
                                        }
                                        font-weight: ${selectedData?.includes(item) ? "700" : "400"};
                                        background-color: ${selectedData?.includes(item) ? theme.neutral50 : "white"};
                                    `}
                                    key={index}
                                    onClick={() => {
                                        if (multiSelect && selectedData?.includes(item)) {
                                            const temp = [...selectedData];
                                            temp.splice(selectedData.indexOf(item), 1);
                                            onChange(temp);
                                        } else if (multiSelect && selectedData) {
                                            const temp = [...selectedData];
                                            temp.push(item);
                                            onChange(temp);
                                        }
                                        if (!multiSelect) {
                                            onChange([item]);
                                            setIsShowAll(false);
                                        }
                                    }}
                                >
                                    {item}
                                </li>
                            );
                        })}
            </ul>
        </div>
    );
}
